import React, { useEffect, useState, Component } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, CreditCard, Lock, AlertCircle } from 'lucide-react';
import { Logo } from './Logo';
interface Invoice {
  id: string;
  invoiceNumber: string;
  customerName: string;
  customerEmail: string;
  lineItems: Array<{
    description: string;
    quantity: number;
    price: number;
    total: number;
  }>;
  subtotal: number;
  discount: number;
  tax: number;
  total: number;
  amountPaid: number;
  balance: number;
  status: string;
}
export function PaymentPage() {
  const {
    invoiceId
  } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    loadInvoice();
  }, [invoiceId, searchParams]);
  const loadInvoice = () => {
    console.log('Loading invoice with ID:', invoiceId);
    try {
      // First, try to get invoice data from URL parameter
      const encodedData = searchParams.get('data');
      if (encodedData) {
        console.log('Found invoice data in URL');
        try {
          const decodedData = decodeURIComponent(atob(encodedData));
          const invoiceData = JSON.parse(decodedData);
          console.log('Decoded invoice data:', invoiceData);
          // Check localStorage for any payment updates
          const stored = localStorage.getItem('invoices');
          if (stored) {
            const invoices = JSON.parse(stored);
            const updated = invoices.find((inv: Invoice) => inv.id === invoiceId);
            if (updated) {
              console.log('Found updated invoice in localStorage');
              setInvoice(updated);
              setPaymentAmount(updated.balance.toFixed(2));
              setLoading(false);
              return;
            }
          }
          // Use the data from URL
          setInvoice(invoiceData);
          setPaymentAmount(invoiceData.balance.toFixed(2));
          setLoading(false);
          return;
        } catch (decodeError) {
          console.error('Error decoding invoice data:', decodeError);
        }
      }
      // Fallback: try localStorage
      console.log('Trying to load from localStorage');
      const stored = localStorage.getItem('invoices');
      if (stored) {
        const invoices = JSON.parse(stored);
        const found = invoices.find((inv: Invoice) => inv.id === invoiceId);
        if (found) {
          console.log('Found invoice in localStorage:', found);
          setInvoice(found);
          setPaymentAmount(found.balance.toFixed(2));
        } else {
          console.error('Invoice not found in localStorage');
        }
      } else {
        console.error('No invoices in localStorage');
      }
    } catch (error) {
      console.error('Error loading invoice:', error);
    } finally {
      setLoading(false);
    }
  };
  const processPayment = async () => {
    if (!invoice) return;
    const amount = parseFloat(paymentAmount);
    if (isNaN(amount) || amount <= 0) {
      setError('Please enter a valid payment amount');
      return;
    }
    if (amount > invoice.balance) {
      setError('Payment amount cannot exceed balance due');
      return;
    }
    setIsProcessing(true);
    setError('');
    // Simulate Stripe payment processing
    setTimeout(() => {
      // Update the invoice
      const newAmountPaid = invoice.amountPaid + amount;
      const newBalance = invoice.total - newAmountPaid;
      const updatedInvoice = {
        ...invoice,
        amountPaid: newAmountPaid,
        balance: newBalance,
        status: newBalance <= 0.01 ? 'Paid in Full' : 'Partial Payment'
      };
      // Save to localStorage
      const stored = localStorage.getItem('invoices');
      if (stored) {
        const invoices = JSON.parse(stored);
        const updated = invoices.map((inv: Invoice) => inv.id === invoiceId ? updatedInvoice : inv);
        localStorage.setItem('invoices', JSON.stringify(updated));
      } else {
        // If no localStorage, create it with this invoice
        localStorage.setItem('invoices', JSON.stringify([updatedInvoice]));
      }
      setInvoice(updatedInvoice);
      setIsProcessing(false);
      setPaymentComplete(true);
      // If there's still a balance, allow another payment
      if (updatedInvoice.balance > 0.01) {
        setTimeout(() => {
          setPaymentAmount(updatedInvoice.balance.toFixed(2));
          setPaymentComplete(false);
        }, 2000);
      }
    }, 2000);
  };
  if (loading) {
    return <div className="min-h-screen bg-slate-100 flex items-center justify-center" data-id="element-304">
        <div className="text-center" data-id="element-305">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" data-id="element-306"></div>
          <p className="text-slate-600" data-id="element-307">Loading invoice...</p>
        </div>
      </div>;
  }
  if (!invoice) {
    return <div className="min-h-screen bg-slate-100" data-id="element-308">
        <div className="bg-white shadow-sm border-b border-slate-200" data-id="element-309">
          <div className="max-w-4xl mx-auto px-6 py-4" data-id="element-310">
            <Logo data-id="element-311" />
          </div>
        </div>
        <div className="max-w-4xl mx-auto p-6 mt-8" data-id="element-312">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center" data-id="element-313">
            <AlertCircle size={48} className="text-red-400 mx-auto mb-4" data-id="element-314" />
            <h1 className="text-2xl font-bold text-slate-800 mb-2" data-id="element-315">
              Invoice Not Found
            </h1>
            <p className="text-slate-600 mb-4" data-id="element-316">
              The invoice you're looking for doesn't exist or the link is
              invalid.
            </p>
            <p className="text-sm text-slate-500 mb-4" data-id="element-317">
              Invoice ID: {invoiceId}
            </p>
            <button onClick={() => window.location.href = '/'} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" data-id="element-318">
              Go to Home
            </button>
          </div>
        </div>
      </div>;
  }
  const isPaidInFull = invoice.balance <= 0.01;
  return <div className="min-h-screen bg-slate-100" data-id="element-319">
      <div className="bg-white shadow-sm border-b border-slate-200" data-id="element-320">
        <div className="max-w-4xl mx-auto px-6 py-4" data-id="element-321">
          <Logo data-id="element-322" />
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-6 mt-8" data-id="element-323">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-324">
          {/* Invoice Details */}
          <div className="lg:col-span-2" data-id="element-325">
            <div className="bg-white rounded-lg shadow-lg p-6" data-id="element-326">
              <div className="mb-6" data-id="element-327">
                <div className="flex items-center justify-between" data-id="element-328">
                  <div data-id="element-329">
                    <h1 className="text-2xl font-bold text-slate-800 mb-2" data-id="element-330">
                      Invoice
                    </h1>
                    <p className="text-slate-600 font-mono" data-id="element-331">
                      {invoice.invoiceNumber}
                    </p>
                  </div>
                  {isPaidInFull && <div className="flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-200 rounded-lg" data-id="element-332">
                      <CheckCircle size={20} className="text-green-600" data-id="element-333" />
                      <span className="text-green-800 font-semibold" data-id="element-334">
                        Paid in Full
                      </span>
                    </div>}
                </div>
              </div>

              <div className="mb-6" data-id="element-335">
                <h2 className="text-sm font-medium text-slate-500 mb-1" data-id="element-336">
                  Bill To
                </h2>
                <p className="font-semibold text-slate-800" data-id="element-337">
                  {invoice.customerName}
                </p>
                <p className="text-slate-600" data-id="element-338">{invoice.customerEmail}</p>
              </div>

              <div className="border-t border-slate-200 pt-6" data-id="element-339">
                <table className="w-full" data-id="element-340">
                  <thead data-id="element-341">
                    <tr className="border-b border-slate-200" data-id="element-342">
                      <th className="text-left py-2 text-sm font-medium text-slate-600" data-id="element-343">
                        Description
                      </th>
                      <th className="text-right py-2 text-sm font-medium text-slate-600" data-id="element-344">
                        Qty
                      </th>
                      <th className="text-right py-2 text-sm font-medium text-slate-600" data-id="element-345">
                        Price
                      </th>
                      <th className="text-right py-2 text-sm font-medium text-slate-600" data-id="element-346">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody data-id="element-347">
                    {invoice.lineItems.map((item, index) => <tr key={index} className="border-b border-slate-100" data-id="element-348">
                        <td className="py-3 text-slate-800" data-id="element-349">
                          {item.description}
                        </td>
                        <td className="py-3 text-right text-slate-600" data-id="element-350">
                          {item.quantity}
                        </td>
                        <td className="py-3 text-right text-slate-600" data-id="element-351">
                          ${item.price.toFixed(2)}
                        </td>
                        <td className="py-3 text-right font-medium text-slate-800" data-id="element-352">
                          ${item.total.toFixed(2)}
                        </td>
                      </tr>)}
                  </tbody>
                </table>

                <div className="mt-6 space-y-2" data-id="element-353">
                  <div className="flex justify-between text-slate-700" data-id="element-354">
                    <span data-id="element-355">Subtotal</span>
                    <span data-id="element-356">${invoice.subtotal.toFixed(2)}</span>
                  </div>
                  {invoice.discount > 0 && <div className="flex justify-between text-green-600" data-id="element-357">
                      <span data-id="element-358">Discount</span>
                      <span data-id="element-359">-${invoice.discount.toFixed(2)}</span>
                    </div>}
                  {invoice.tax > 0 && <div className="flex justify-between text-slate-700" data-id="element-360">
                      <span data-id="element-361">Tax</span>
                      <span data-id="element-362">${invoice.tax.toFixed(2)}</span>
                    </div>}
                  <div className="flex justify-between font-bold text-lg text-slate-900 pt-2 border-t border-slate-200" data-id="element-363">
                    <span data-id="element-364">Total</span>
                    <span data-id="element-365">${invoice.total.toFixed(2)}</span>
                  </div>
                  {invoice.amountPaid > 0 && <>
                      <div className="flex justify-between text-green-600 pt-2" data-id="element-366">
                        <span data-id="element-367">Amount Paid</span>
                        <span className="font-semibold" data-id="element-368">
                          ${invoice.amountPaid.toFixed(2)}
                        </span>
                      </div>
                      {!isPaidInFull && <div className="flex justify-between font-bold text-xl text-red-600 pt-2 border-t border-slate-200" data-id="element-369">
                          <span data-id="element-370">Balance Due</span>
                          <span data-id="element-371">${invoice.balance.toFixed(2)}</span>
                        </div>}
                    </>}
                </div>
              </div>
            </div>
          </div>

          {/* Payment Form or Paid Status */}
          <div className="lg:col-span-1" data-id="element-372">
            <div className="bg-white rounded-lg shadow-lg p-6 sticky top-6" data-id="element-373">
              {isPaidInFull ? <div className="text-center py-8" data-id="element-374">
                  <CheckCircle size={64} className="text-green-500 mx-auto mb-4" data-id="element-375" />
                  <h3 className="text-xl font-bold text-green-800 mb-2" data-id="element-376">
                    Payment Complete
                  </h3>
                  <p className="text-slate-600 text-sm mb-6" data-id="element-377">
                    This invoice has been paid in full. Thank you for your
                    payment!
                  </p>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4" data-id="element-378">
                    <div className="text-sm text-green-800" data-id="element-379">
                      <p className="font-semibold mb-1" data-id="element-380">Payment Summary</p>
                      <div className="flex justify-between mt-2" data-id="element-381">
                        <span data-id="element-382">Total Paid:</span>
                        <span className="font-bold" data-id="element-383">
                          ${invoice.amountPaid.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div> : paymentComplete ? <div className="text-center py-8" data-id="element-384">
                  <CheckCircle size={48} className="text-green-500 mx-auto mb-4" data-id="element-385" />
                  <h3 className="text-lg font-bold text-green-800 mb-2" data-id="element-386">
                    Payment Successful!
                  </h3>
                  <p className="text-slate-600 text-sm" data-id="element-387">
                    {invoice.balance > 0.01 ? 'You can make another payment for the remaining balance.' : 'Thank you for your payment!'}
                  </p>
                </div> : <>
                  <h3 className="text-lg font-bold text-slate-800 mb-4" data-id="element-388">
                    Make a Payment
                  </h3>

                  <div className="mb-4" data-id="element-389">
                    <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-390">
                      Payment Amount
                    </label>
                    <div className="relative" data-id="element-391">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" data-id="element-392">
                        $
                      </span>
                      <input type="number" value={paymentAmount} onChange={e => setPaymentAmount(e.target.value)} className="w-full pl-8 pr-3 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg font-semibold" step="0.01" min="0" max={invoice.balance} data-id="element-393" />
                    </div>
                    <p className="text-xs text-slate-500 mt-1" data-id="element-394">
                      Maximum: ${invoice.balance.toFixed(2)}
                    </p>
                  </div>

                  {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-id="element-395">
                      {error}
                    </div>}

                  <button onClick={processPayment} disabled={isProcessing} className={`w-full py-3 px-4 rounded-lg font-medium flex items-center justify-center gap-2 ${isProcessing ? 'bg-slate-400 text-white cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'} transition-colors`} data-id="element-396">
                    {isProcessing ? <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" data-id="element-397"></div>
                        Processing...
                      </> : <>
                        <CreditCard size={20} data-id="element-398" />
                        Pay with Card
                      </>}
                  </button>

                  <div className="mt-4 flex items-center justify-center text-xs text-slate-500" data-id="element-399">
                    <Lock size={12} className="mr-1" data-id="element-400" />
                    Secure payment powered by Stripe
                  </div>
                </>}
            </div>
          </div>
        </div>
      </div>
    </div>;
}